function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function activateSideDrawer(element, activeClass = 'active') {
  if (!element) return;

  element.classList.remove(activeClass);
  void element.offsetWidth;

  requestAnimationFrame(() => {
    element.classList.add(activeClass);
  });
}

// Ref-counted body scroll lock shared by drawers and modals, so closing one
// overlay can't unlock the page while another is still open.
const bodyScrollLockOwners = new Set();
const videosPausedByDrawer = new WeakSet();

function pausePageVideos() {
  document.querySelectorAll('video').forEach((video) => {
    if (video.paused) return;
    videosPausedByDrawer.add(video);
    video.pause();
  });
}

function resumePageVideos() {
  document.querySelectorAll('video').forEach((video) => {
    if (!videosPausedByDrawer.has(video)) return;
    videosPausedByDrawer.delete(video);
    const rect = video.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > window.innerHeight) return;
    const play = video.play();
    if (play && typeof play.catch === 'function') play.catch(() => {});
  });
}

window.lockBodyScroll = (owner) => {
  bodyScrollLockOwners.add(owner);
  document.body.classList.add('overflow-hidden');
  document.documentElement.classList.add('drawer-open');
  pausePageVideos();
};
window.unlockBodyScroll = (owner) => {
  bodyScrollLockOwners.delete(owner);
  if (bodyScrollLockOwners.size === 0) {
    document.body.classList.remove('overflow-hidden');
    document.documentElement.classList.remove('drawer-open');
    resumePageVideos();
  }
};
// Hard reset of the lock, ignoring the owner set. For cases where the page
// state is being torn down out-of-band and the ref count can't be trusted:
// the theme editor removing a section while a drawer is open, or the Podium
// widget mutating <html>/<body> after its overlay closed.
window.forceUnlockBodyScroll = () => {
  bodyScrollLockOwners.clear();
  document.body.classList.remove('overflow-hidden');
  document.documentElement.classList.remove('drawer-open');
  resumePageVideos();
};

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => {
    // Ambient product videos autoplay on an infinite loop and should never pause.
    if (video.closest('.product__media--video-autoplay')) return;
    video.pause();
  });
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

// Ambient product videos: enforce muted looping autoplay even if the browser
// ignores the HTML attributes (e.g. Safari's stricter autoplay policies).
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.product__media--video-autoplay video').forEach((video) => {
    video.muted = true;
    video.loop = true;
    video.setAttribute('playsinline', '');
    const playPromise = video.play();
    if (playPromise) playPromise.catch(() => {});
  });
});

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    // The only Escape path for modals. A <ds-drawer> opened above the modal
    // owns Escape until it closes.
    this.onEscapeKeydown = (event) => {
      if (event.key !== 'Escape' || !this.hasAttribute('open') || window.DsDrawer?.top) return;
      event.preventDefault();
      this.hide();
    };
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    window.lockBodyScroll('modal');
    this.setAttribute('open', '');
    document.addEventListener('keydown', this.onEscapeKeydown);
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    if (!this.hasAttribute('open')) return;
    document.removeEventListener('keydown', this.onEscapeKeydown);
    window.unlockBodyScroll('modal');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.onVariantSelectChange = this.onVariantSelectChange.bind(this);
  }

  connectedCallback() {
    if (this.dataset.availabilityBound !== 'true') {
      this.dataset.availabilityBound = 'true';
      this.addEventListener('change', this.onVariantSelectChange);
      this.onFirstInteraction = this.onFirstInteraction || (() => this.loadFullVariantData());
      ['pointerdown', 'focusin', 'change'].forEach((type) =>
        this.addEventListener(type, this.onFirstInteraction, { once: true, passive: true })
      );
    }

    // During HTML parse, connectedCallback can run before this element's
    // children exist. Wait a frame so Diamond Type radios and the variant
    // JSON are in the DOM before we pin Solid Gold → Natural and sync price.
    requestAnimationFrame(() => this.initializeOptionState());
  }

  initializeOptionState() {
    if (!this.isConnected) return;

    // Both calls return the control they moved (or null when nothing moved).
    const solidGoldMoved = this.syncSolidGoldDiamondType();
    const availabilityMoved = this.updateOptionAvailability({ syncSelection: true });

    // The initial sync can silently move the checked radio / selected option
    // (e.g. the server-selected variant is sold out, or Solid Gold is pinned
    // from Lab to Natural). Without a follow-up update, price, media,
    // add-to-cart, and the hidden input[name="id"] keep showing the old
    // variant. The letter pendant builder manages its own selection state,
    // so it is excluded.
    //
    // Only run the correction when a selection actually moved. Otherwise the
    // server-rendered variant already matches the DOM and the correction would
    // needlessly rewrite the URL with ?variant= on every page load.
    if (!this.isLetterPendantMode() && (solidGoldMoved || availabilityMoved)) {
      this.applyInitialSyncCorrection();
    }
  }

  applyInitialSyncCorrection() {
    if (this._initialSyncCorrectionQueued) return;
    this._initialSyncCorrectionQueued = true;

    // Wait until product-info and product-form are upgraded; global.js runs
    // before those deferred scripts, so their methods don't exist yet.
    Promise.all([
      customElements.whenDefined('product-info'),
      customElements.whenDefined('product-form'),
    ]).then(() => {
      if (!this.isConnected) return;

      const productInfo = this.closest('product-info');
      if (!productInfo?.applyInstantVariantUpdate) return;

      const variant = this.getCurrentVariant();
      if (!variant) return;

      productInfo.applyInstantVariantUpdate(variant, productInfo.dataset.url);
    });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onVariantSelectChange);
    // Clear the bound flag so a moved (disconnected + reconnected) element
    // re-binds its change listener in connectedCallback.
    delete this.dataset.availabilityBound;
  }

  onVariantSelectChange(event) {
    const target = this.getInputForEventTarget(event.target);
    this.updateSelectionMetadata({ target });
    this.syncSolidGoldDiamondType();
    this.updateOptionAvailability({ syncSelection: true });

    publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
      data: {
        event,
        target,
        selectedOptionValues: this.selectedOptionValues,
      },
    });
  }

  isSolidGoldValue(value) {
    const normalized = String(value || '').trim().toLowerCase();
    // Plain is a no-diamond size, same as Solid Gold: Diamond Type does not apply.
    return normalized === 'solid gold' || normalized === 'plain';
  }

  hasSolidGoldSelection() {
    return Object.values(this.getSelectedOptionsByPosition()).some((value) => this.isSolidGoldValue(value));
  }

  findNaturalDiamondControl(group) {
    const radios = Array.from(group.querySelectorAll('input[type="radio"]'));
    const naturalRadio =
      radios.find((input) => input.value.trim().toLowerCase() === 'natural diamond') ||
      radios.find((input) => /natural/i.test(input.value));
    if (naturalRadio) return naturalRadio;

    const select = group.querySelector('select');
    if (!select) return null;
    return (
      Array.from(select.options).find((option) => option.value.trim().toLowerCase() === 'natural diamond') ||
      Array.from(select.options).find((option) => /natural/i.test(option.value)) ||
      null
    );
  }

  /**
   * Solid Gold (Size or Style) has no diamonds. Hide Diamond Type and pin
   * Natural Diamond so the line price is the real Solid Gold SKU, not the
   * Lab twin that was generated for the option matrix.
   */
  syncSolidGoldDiamondType() {
    const group = this.querySelector('[data-option-name="diamond type"]');
    if (!group) return null;

    const hide = this.hasSolidGoldSelection();
    const wasHidden = group.hidden;

    if (hide && !wasHidden) {
      const checked = group.querySelector('input[type="radio"]:checked');
      const select = group.querySelector('select');
      this._diamondTypeBeforeSolidGold = checked?.value || select?.value || '';
    }

    group.hidden = hide;
    group.setAttribute('aria-hidden', hide ? 'true' : 'false');

    if (hide) {
      const natural = this.findNaturalDiamondControl(group);
      if (!natural) return null;

      if (natural.matches('input[type="radio"]')) {
        if (natural.checked) return null;
        natural.disabled = false;
        natural.classList.remove('disabled');
        natural.checked = true;
        this.updateSelectionMetadata({ target: natural });
        return natural;
      }

      const select = natural.closest('select');
      if (select && select.value !== natural.value) {
        select.value = natural.value;
        natural.setAttribute('selected', 'selected');
        this.updateSelectionMetadata({ target: natural });
        return natural;
      }
      return null;
    }

    if (wasHidden && this._diamondTypeBeforeSolidGold) {
      const restoreRadio = Array.from(group.querySelectorAll('input[type="radio"]')).find(
        (input) => input.value === this._diamondTypeBeforeSolidGold && !input.disabled
      );
      if (restoreRadio && !restoreRadio.checked) {
        restoreRadio.checked = true;
        this.updateSelectionMetadata({ target: restoreRadio });
        return restoreRadio;
      }

      const select = group.querySelector('select');
      const restoreOption = select
        ? Array.from(select.options).find(
            (option) => option.value === this._diamondTypeBeforeSolidGold && !option.disabled
          )
        : null;
      if (select && restoreOption && select.value !== restoreOption.value) {
        select.value = restoreOption.value;
        restoreOption.setAttribute('selected', 'selected');
        this.updateSelectionMetadata({ target: restoreOption });
        return restoreOption;
      }
    }

    return null;
  }

  getVariantData() {
    if (this._fullVariantData) return this._fullVariantData;
    if (this._variantData) return this._variantData;

    const source = this.querySelector('[data-product-variants]');
    if (!source?.textContent) {
      this._variantData = [];
      return this._variantData;
    }

    try {
      this._variantData = JSON.parse(source.textContent).map((variant) => {
        if (Array.isArray(variant.options) && !('option1' in variant)) {
          variant.option1 = variant.options[0] ?? null;
          variant.option2 = variant.options[1] ?? null;
          variant.option3 = variant.options[2] ?? null;
        }
        return variant;
      });
    } catch (error) {
      this._variantData = [];
    }

    return this._variantData;
  }

  // The inline map is trimmed (and empty above 250 variants); the full map
  // from /products/{handle}.js replaces it once the shopper starts picking.
  loadFullVariantData() {
    const url = this.dataset.productUrl;
    if (!url || this._fullVariantData || this._fullVariantRequest) return this._fullVariantRequest;

    const hadInlineData = this.getVariantData().length > 0;
    this._fullVariantRequest = fetch(`${url}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((product) => {
        if (!Array.isArray(product?.variants) || !product.variants.length) return;
        this._fullVariantData = product.variants;
        if (!hadInlineData && this.isConnected) this.updateOptionAvailability({ syncSelection: false });
      })
      .catch(() => {})
      .finally(() => {
        this._fullVariantRequest = null;
      });

    return this._fullVariantRequest;
  }

  isLetterPendantMode() {
    return this.dataset.letterPendant === 'true';
  }

  isLetterPendantManagedGroup(group) {
    return this.isLetterPendantMode() && !!group.closest('letter-pendant-builder');
  }

  getSelectedOptionsByPosition() {
    const selected = {};

    this.querySelectorAll('[data-option-position]').forEach((group) => {
      const position = parseInt(group.dataset.optionPosition, 10);
      if (!position) return;

      const checked = group.querySelector('input[type="radio"]:checked');
      const select = group.querySelector('select');

      if (checked) {
        selected[position] = checked.value;
      } else if (select?.value) {
        selected[position] = select.value;
      }
    });

    return selected;
  }

  isOptionValueAvailable(variants, targetPosition, targetValue, selectedByPosition) {
    return variants.some((variant) => {
      if (!variant.available) return false;

      const optionKey = `option${targetPosition}`;
      if (variant[optionKey] !== targetValue) return false;

      return Object.entries(selectedByPosition).every(([position, value]) => {
        const optionPosition = parseInt(position, 10);
        if (optionPosition === targetPosition || !value) return true;
        return variant[`option${optionPosition}`] === value;
      });
    });
  }

  setInputAvailability(input, isAvailable) {
    input.classList.toggle('disabled', !isAvailable);
    input.disabled = !isAvailable;
  }

  syncFieldsetSelection(fieldset) {
    const checked = fieldset.querySelector('input[type="radio"]:checked');
    if (checked && !checked.disabled) return null;

    const firstAvailable = fieldset.querySelector('input[type="radio"]:not(:disabled)');
    if (firstAvailable) {
      firstAvailable.checked = true;
      return firstAvailable;
    }

    if (checked) checked.checked = false;
    return null;
  }

  syncSelectSelection(select) {
    const selectedOption = select.selectedOptions[0];
    if (selectedOption && !selectedOption.disabled) return null;

    const firstAvailable = Array.from(select.options).find((option) => !option.disabled);
    if (firstAvailable) {
      select.value = firstAvailable.value;
      firstAvailable.setAttribute('selected', 'selected');
      return firstAvailable;
    }

    return null;
  }

  // Returns the input/option whose selection was changed by syncSelection,
  // or null when the visual selection was left untouched.
  updateOptionAvailability({ syncSelection = false } = {}) {
    const variants = this.getVariantData();
    if (!variants.length) return null;

    const selectedByPosition = this.getSelectedOptionsByPosition();
    let syncedTarget = null;

    this.querySelectorAll('[data-option-position]').forEach((group) => {
      const position = parseInt(group.dataset.optionPosition, 10);
      if (!position) return;

      group.querySelectorAll('input[type="radio"]').forEach((input) => {
        const isAvailable = this.isOptionValueAvailable(
          variants,
          position,
          input.value,
          selectedByPosition
        );
        this.setInputAvailability(input, isAvailable);
      });

      group.querySelectorAll('select option').forEach((option) => {
        if (!option.value) return;

        const isAvailable = this.isOptionValueAvailable(
          variants,
          position,
          option.value,
          selectedByPosition
        );
        option.disabled = !isAvailable;
        option.classList.toggle('disabled', !isAvailable);
      });

      if (!syncSelection) return;

      if (group.matches('fieldset') && !this.isLetterPendantManagedGroup(group)) {
        syncedTarget = this.syncFieldsetSelection(group) || syncedTarget;
      }

      const select = group.querySelector('select');
      if (select) {
        syncedTarget = this.syncSelectSelection(select) || syncedTarget;
      }
    });

    if (syncedTarget) {
      this.updateSelectionMetadata({ target: syncedTarget });
    }

    this.updateOptionPrices();

    return syncedTarget;
  }

  isLabGrownVariant(variant) {
    return [variant.option1, variant.option2, variant.option3].some((value) =>
      String(value || '').toLowerCase().includes('lab grown')
    );
  }

  isSolidGoldVariant(variant) {
    return [variant.option1, variant.option2, variant.option3].some((value) => this.isSolidGoldValue(value));
  }

  /**
   * Each pill's "From $x" is the price of the variant that pill would select
   * given every other current choice — so 0.10ctw reads the Natural price
   * once Natural is picked. Mirrors the Liquid in product-variant-options;
   * keep the two in step. Falls back to the cheapest variant carrying the
   * value when that exact combination does not exist.
   */
  updateOptionPrices() {
    const variants = this.getVariantData();
    if (!variants.length) return;

    const selected = this.getSelectedOptionsByPosition();
    const diamondGroup = this.querySelector('[data-option-name="diamond type"]');
    const diamondPosition = diamondGroup ? parseInt(diamondGroup.dataset.optionPosition, 10) : null;

    // While Solid Gold pins Natural, leaving it restores the earlier Diamond
    // Type, so the other pills should quote that restored choice.
    if (diamondPosition && this._diamondTypeBeforeSolidGold && this.hasSolidGoldSelection()) {
      selected[diamondPosition] = this._diamondTypeBeforeSolidGold;
    }

    this.querySelectorAll('[data-option-position]').forEach((group) => {
      const position = parseInt(group.dataset.optionPosition, 10);
      if (!position) return;

      const isDiamondTypeGroup = position === diamondPosition;

      group.querySelectorAll('input[type="radio"]').forEach((input) => {
        const priceEl = input.id
          ? group.querySelector(`label[for="${CSS.escape(input.id)}"] .product-form__pill-from-price`)
          : null;
        if (!priceEl) return;

        const value = input.value;
        const valueIsSolidGold = this.isSolidGoldValue(value);
        let matchedMin = null;
        let fallbackMin = null;

        variants.forEach((variant) => {
          if (variant[`option${position}`] !== value) return;
          if (valueIsSolidGold && this.isLabGrownVariant(variant)) return;
          if (isDiamondTypeGroup && this.isSolidGoldVariant(variant)) return;

          if (fallbackMin === null || variant.price < fallbackMin) fallbackMin = variant.price;

          const matchesSelection = Object.entries(selected).every(([key, selectedValue]) => {
            const otherPosition = parseInt(key, 10);
            if (otherPosition === position || !selectedValue) return true;
            // Solid Gold always resolves to Natural, whatever Diamond Type shows.
            if (valueIsSolidGold && otherPosition === diamondPosition) return true;
            return variant[`option${otherPosition}`] === selectedValue;
          });

          if (matchesSelection && (matchedMin === null || variant.price < matchedMin)) {
            matchedMin = variant.price;
          }
        });

        const cents = matchedMin ?? fallbackMin;
        if (cents === null) return;

        const text = this.formatFromPrice(cents);
        if (priceEl.textContent !== text) priceEl.textContent = text;
      });
    });

    this.updateOptionStockMarks(variants, selected, diamondPosition);
  }

  /**
   * Ready-to-ship check on each pill: shown when the variant that pill would
   * select, given every other current choice, is in stock. The id list comes
   * from the Liquid in product-variant-options (same rule as the PDP badge).
   */
  updateOptionStockMarks(variants, selected, diamondPosition) {
    this.querySelectorAll('[data-option-position]').forEach((group) => {
      const idsEl = group.querySelector('[data-pill-stock-ids]');
      if (!idsEl) return;

      const position = parseInt(group.dataset.optionPosition, 10);
      if (!position) return;

      const inStockIds = idsEl.dataset.pillStockIds || '';

      group.querySelectorAll('input[type="radio"]').forEach((input) => {
        const markEl = input.id
          ? group.querySelector(`label[for="${CSS.escape(input.id)}"] [data-pill-stock]`)
          : null;
        if (!markEl) return;

        const value = input.value;
        const valueIsSolidGold = this.isSolidGoldValue(value);

        const ready = variants.some((variant) => {
          if (variant[`option${position}`] !== value) return false;
          if (!inStockIds.includes(`,${variant.id},`)) return false;

          return Object.entries(selected).every(([key, selectedValue]) => {
            const otherPosition = parseInt(key, 10);
            if (otherPosition === position || !selectedValue) return true;
            if (valueIsSolidGold && otherPosition === diamondPosition) return true;
            return variant[`option${otherPosition}`] === selectedValue;
          });
        });

        if (markEl.hidden === ready) markEl.hidden = !ready;
      });
    });
  }

  // Same output as snippets/variant-option-from-price.liquid ("From $450",
  // "From $1.3k" — truncated, not rounded).
  formatFromPrice(cents) {
    const value = Math.floor(Number(cents) || 0);

    if (value >= 100000) {
      const whole = Math.floor(value / 100000);
      const fraction = Math.floor((value % 100000) / 10000);
      return fraction === 0 ? `From $${whole}k` : `From $${whole}.${fraction}k`;
    }

    return `From $${Math.floor(value / 100)}`;
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        ?.removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`)?.querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    } else if (tagName === 'OPTION') {
      const select = target.closest('select');
      if (!select?.selectedOptions.length) return;
      const selectedSwatchValue = select.closest('.product-form__input')?.querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = select.selectedOptions[0].textContent;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  getCurrentVariant() {
    const variants = this.getVariantData();
    if (!variants.length) return null;

    const selected = this.getSelectedOptionsByPosition();
    const selectedPositions = Object.keys(selected).filter((position) => selected[position]);

    if (!selectedPositions.length) return null;

    if (this.isLetterPendantMode()) {
      const requiredPositions = new Set(
        Array.from(this.querySelectorAll('[data-option-position]'))
          .map((group) => parseInt(group.dataset.optionPosition, 10))
          .filter(Boolean)
          .map(String)
      );

      if (selectedPositions.length < requiredPositions.size) return null;
    }

    return (
      variants.find((variant) =>
        selectedPositions.every((position) => variant[`option${position}`] === selected[position])
      ) || null
    );
  }

  updateSelectedVariant(variant) {
    const selectedVariantScript = this.querySelector('[data-selected-variant]');
    if (selectedVariantScript && variant) {
      selectedVariantScript.textContent = JSON.stringify(variant);
    }
    this.updateOptionPrices();
  }

  refreshFromServer() {
    this._variantData = null;
    this.updateOptionAvailability({ syncSelection: false });
    this.syncSelectionMetadata();
  }

  syncSelectionMetadata() {
    this.querySelectorAll('[data-option-position]').forEach((group) => {
      const checked = group.querySelector('input[type="radio"]:checked');
      if (checked) {
        this.updateSelectionMetadata({ target: checked });
        return;
      }

      const select = group.querySelector('select');
      if (select?.selectedOptions[0]) {
        this.updateSelectionMetadata({ target: select.selectedOptions[0] });
      }
    });
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll('select option[selected], fieldset input[type="radio"]:checked:not(:disabled)')
    ).map(({ dataset }) => dataset.optionValueId);
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  // These marks/measures are only useful when something reads them in the
  // Performance panel. Nothing in the theme consumes them, so skip the work by
  // default and gate it behind an opt-in flag (set `window.CART_PERFORMANCE_DEBUG = true`
  // in the console to profile). The wrapped work in `measure()` always runs.
  static get #enabled() {
    return typeof window !== 'undefined' && window.CART_PERFORMANCE_DEBUG === true;
  }

  static createStartingMarker(benchmarkName) {
    if (!CartPerformance.#enabled) return null;
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    if (!CartPerformance.#enabled) return;
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    if (!CartPerformance.#enabled || !startMarker) return;
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    if (!CartPerformance.#enabled) {
      callback();
      return;
    }
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}
